import React, { useState, useEffect } from "react";
import Layout, { Container } from "../components/layout";
import { BiUser } from "react-icons/bi";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { apiurl } from "../config/config";
import { X, Plus } from "lucide-react";
import { useUser } from "../config/userProvider";

export default function PayrollDashboard() {
  const { userData } = useUser();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (startDate)
          params.append("startDate", startDate.toISOString().split("T")[0]);
        if (endDate)
          params.append("endDate", endDate.toISOString().split("T")[0]);

        const response = await axios.get(
          `${apiurl}/api/dashboard/summary-dashboard?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
          }
        );

        if (response.data.success) {
          setDashboardData(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [startDate, endDate]);

  if (loading) return <p className="p-4">Loading dashboard...</p>;

  const total = dashboardData?.total || {};
  const range = dashboardData?.range || {};

  return (
    <Layout>
      <Container overflow={"overflow-scroll"}>
        <div className="flex min-h-screen bg-[#f5f7fb] text-gray-800">
          <main className="flex-1 px-6 py-6 overflow-y-auto">
            <div className="flex justify-between items-center w-full bg-white rounded-2xl shadow mb-6 p-4">
            <div className="px-6 py-4">
              <h1 className="text-2xl font-semibold">Payroll Dashboard</h1>
              <p className="text-gray-600 mt-1">
                Overview of payroll and employee statistics
              </p>
            </div>
            <CreateAnnouncementModal userObjId={userData?._id} />

          </div>
            {/* Date Filter */}
            <div className="flex gap-2 mb-4">
              <DatePicker selected={startDate} onChange={setStartDate} className="border border-gray-300 rounded-md p-2" />
              <DatePicker selected={endDate} onChange={setEndDate} className="border border-gray-300 rounded-md p-2" />
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
              <StatCard
                title="Total Applications"
                value={total.allApplications}
                color="bg-[#7DA0FA]"
              />
              <StatCard
                title="Total Candidates"
                value={total.allCandidates}
                color="bg-[#F3797E]"
              />
              <StatCard
                title="Total Employees"
                value={total.allEmployees}
                color="bg-[#7978E9]"
              />
              <StatCard
                title="Trainees"
                value={total.allOnTrainees}
                color="bg-[#98BDFF]"
              />
            </div>

            {/* Employee Status */}
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
              {Object.entries(total.employeeStatusCounts || {}).map(
                ([status, count]) => (
                  <StatCard
                    key={status}
                    title={status.charAt(0).toUpperCase() + status.slice(1)}
                    value={count}
                    color={
                      {
                        active: "bg-green-500",
                        inactive: "bg-gray-400",
                        resigned: "bg-yellow-400",
                        terminated: "bg-red-500",
                        absconded: "bg-red-700",
                      }[status] || "bg-gray-300"
                    }
                  />
                )
              )}
            </div>

            {/* Application Status */}
            <div className="bg-white p-5 rounded-2xl shadow mb-6">
              <h2 className="text-lg font-semibold mb-3">Application Status</h2>
              <div className="flex flex-wrap gap-2">
                {["total", "hired", "training", "joined", "absconded"].map(
                  (status) => (
                    <span
                      key={status}
                      className={`px-3 py-1 rounded-full text-white ${
                        {
                          total: "bg-blue-500",
                          hired: "bg-green-500",
                          training: "bg-yellow-400 text-black",
                          joined: "bg-violet-500",
                          absconded: "bg-red-500",
                        }[status]
                      }`}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}:{" "}
                      {range.statusCounts?.[status] ??
                        total.statusCounts?.[status] ??
                        0}
                    </span>
                  )
                )}
              </div>
            </div>

            {/* Birthdays, Events, Anniversaries */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <InfoCard
                title="Upcoming Birthdays"
                items={total.upcomingBirthdays || []}
              />
              <InfoCard
                title="Upcoming Events"
                items={total.upcomingEvents || []}
              />
              <InfoCard
                title="Anniversaries"
                items={total.anniversaries || []}
              />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl shadow p-5">
                <h2 className="text-lg font-semibold mb-3">Payroll Overview</h2>
                <img
                  src="/sample-bar-chart.png"
                  alt="Payroll Chart"
                  className="w-full rounded"
                />
              </div>
              <div className="bg-white rounded-2xl shadow p-5">
                <h2 className="text-lg font-semibold mb-3">
                  Attendance Overview
                </h2>
                <img
                  src="/sample-line-chart.png"
                  alt="Attendance Chart"
                  className="w-full rounded"
                />
              </div>
            </div>
          </main>
        </div>
      </Container>
    </Layout>
  );
}

function StatCard({ title, value, color }) {
  return (
    <div className={`${color} text-white rounded-2xl shadow p-5`}>
      <h3 className="text-sm font-medium">{title}</h3>
      <p className="text-xl font-bold mt-1">{value}</p>
    </div>
  );
}

function InfoCard({ title, items }) {
  return (
    <div className="bg-white rounded-2xl shadow p-5">
      <h3 className="text-lg font-semibold mb-2">{title}</h3>
      <ul className="text-sm text-gray-600 list-disc list-inside">
        {items.map((item, idx) => (
          <li key={idx}>{item}</li>
        ))}
      </ul>
    </div>
  );
}


const CreateAnnouncementModal = ({ userObjId }) => {
  const [show, setShow] = useState(false);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  const submit = async () => {
    if (!title || !message || !expiresAt) return alert("All fields required");

    try {
      await axios.post(`${apiurl}/api/announcement/create`, {
        title,
        message,
        expiresAt,
        userObjId,
      });

      setTitle("");
      setMessage("");
      setExpiresAt("");
      setShow(false);
      alert("Announcement Created Successfully");
    } catch (err) {
      console.error(err);
      alert("Failed to Create Announcement");
    }
  };

  return (
    <>
      {/* Button to open modal */}
      <button
        onClick={() => setShow(true)}
        className="flex items-center cursor-pointer gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm px-4 py-2 rounded-md shadow transition"
      >
        <Plus size={18} /> Create Announcement
      </button>

      {show && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50">
          <div className="bg-white w-full max-w-3xl rounded-xl shadow-lg p-6 relative animate-fadeIn">
            <button
              className="absolute top-3 right-3 text-gray-600 hover:text-black"
              onClick={() => setShow(false)}
            >
              <X size={20} />
            </button>

            <h2 className="text-lg font-semibold mb-4 text-gray-800">
              Create Announcement
            </h2>

            <div className="flex flex-col gap-3">
              <input
                className="border rounded-md p-2 text-sm"
                placeholder="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />

              <textarea
                className="border rounded-md p-2 text-sm min-h-[80px]"
                placeholder="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />

              <div className="text-sm">
                <label className="text-gray-600">Expiry Date & Time</label>
                <input
                  type="datetime-local"
                  className="border rounded-md p-2 w-full mt-1"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
              </div>

              <button
                onClick={submit}
                className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm py-2 cursor-pointer rounded-md shadow-md transition"
              >
                Save Announcement
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
